import React, { useState } from 'react'
import { approveReadiness } from './blockChangeHelpers'
import { FindingLine } from './ChangeRequestValidation'

function Dialog({ title, testId, onClose, children, footer }) {
  return (
    <div className="modal is-active" data-testid={testId}>
      <div className="modal-background" onClick={onClose} aria-hidden="true" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title is-size-5">{title}</p>
          <button
            type="button"
            className="delete"
            aria-label="Close"
            onClick={onClose}
          />
        </header>
        <section className="modal-card-body">{children}</section>
        <footer className="modal-card-foot">{footer}</footer>
      </div>
    </div>
  )
}

// The warnings the approver saw are sent back; the API refuses the apply if they have changed since.
// The tick belongs to one set of warnings and operations, so a reload that changes either leaves it unticked.
export function ApproveDialog({
  validation,
  operationsHash,
  isBusy,
  onConfirm,
  onClose,
}) {
  const warningCount = validation.warnings.length
  const warningsKey = `${operationsHash ?? ''}:${
    validation.warnings_fingerprint ?? ''
  }:${warningCount}`
  const [acknowledgement, setAcknowledgement] = useState({
    warningsKey,
    checked: false,
  })
  const acknowledged =
    acknowledgement.warningsKey === warningsKey && acknowledgement.checked
  const setAcknowledged = (checked) =>
    setAcknowledgement({ warningsKey, checked })
  const [comment, setComment] = useState('')
  const { ready, reason } = approveReadiness({
    validation,
    acknowledged,
    comment,
  })

  const confirm = () => {
    onConfirm({
      acknowledged_warning_count: warningCount,
      acknowledged_warnings_fingerprint: validation.warnings_fingerprint,
      comment,
    })
  }
  const footer = (
    <>
      <button
        type="button"
        className="button is-primary"
        disabled={!ready || isBusy}
        onClick={confirm}
        data-testid="cr-approve-confirm"
      >
        Approve and apply
      </button>
      <button type="button" className="button" onClick={onClose}>
        Cancel
      </button>
    </>
  )

  return (
    <Dialog
      title="Approve and apply"
      testId="cr-approve-dialog"
      onClose={onClose}
      footer={footer}
    >
      <p className="mb-3">
        This writes the changes to the live block tables. Underwriting picks
        them up when the DS cache is cleared, at the latest within an hour.
      </p>
      {[...validation.errors, ...validation.warnings].map((finding, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <FindingLine key={`${finding.code}-${index}`} finding={finding} />
      ))}
      {warningCount > 0 && (
        <label className="checkbox mt-2 mb-3 is-block">
          <input
            type="checkbox"
            className="mr-2"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            data-testid="cr-approve-acknowledge"
          />
          I reviewed the {warningCount} warning{warningCount === 1 ? '' : 's'}
        </label>
      )}
      <textarea
        className="textarea"
        rows={3}
        placeholder="Approval comment (required)"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        data-testid="cr-approve-comment"
      />
      {reason && <p className="help is-danger">{reason}</p>}
    </Dialog>
  )
}

export function CommentDialog({
  title,
  confirmLabel,
  isBusy,
  onConfirm,
  onClose,
}) {
  const [comment, setComment] = useState('')

  const footer = (
    <>
      <button
        type="button"
        className="button is-primary"
        disabled={isBusy || comment.trim() === ''}
        onClick={() => onConfirm({ comment })}
        data-testid="cr-comment-confirm"
      >
        {confirmLabel}
      </button>
      <button type="button" className="button" onClick={onClose}>
        Cancel
      </button>
    </>
  )

  return (
    <Dialog
      title={title}
      testId="cr-comment-dialog"
      onClose={onClose}
      footer={footer}
    >
      <textarea
        className="textarea"
        rows={3}
        placeholder="Comment (required)"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        data-testid="cr-comment-text"
      />
    </Dialog>
  )
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  isBusy,
  onConfirm,
  onClose,
}) {
  const footer = (
    <>
      <button
        type="button"
        className="button is-danger"
        disabled={isBusy}
        onClick={onConfirm}
        data-testid="cr-confirm-confirm"
      >
        {confirmLabel}
      </button>
      <button type="button" className="button" onClick={onClose}>
        Cancel
      </button>
    </>
  )

  return (
    <Dialog
      title={title}
      testId="cr-confirm-dialog"
      onClose={onClose}
      footer={footer}
    >
      <p>{message}</p>
    </Dialog>
  )
}
